import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /**
   * Whether the real add to cart button is currently intersecting the viewport.
   * Starts true so nothing shows before the observer has reported once.
   * @type {boolean}
   */
  #targetVisible = true;

  /** @type {boolean} */
  #footerVisible = false;

  /**
   * Locket photo gate state. Null means no gate applies to this product.
   * @type {{ready: boolean, label: string} | null}
   */
  #gate = null;

  /** @type {string | null} */
  #ungatedLabel = null;

  /** True between our click and the cart's answer, so the answer can be read. */
  #awaitingAdd = false;

  /** @type {ResizeObserver | null} */
  #columnObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller each time, so a disconnect and reconnect (a section
    // re-render, a move in the editor) attaches every listener again rather
    // than handing them a signal that was aborted on the way out.
    this.#abortController = new AbortController();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    // Keeps the bar centred on the details column on desktop. The section
    // element is never replaced by a variant morph, and its width moves with
    // every change that can move the column.
    if (target) {
      this.#columnObserver = new ResizeObserver(() => this.#alignToDetails());
      this.#columnObserver.observe(target);
    }
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Locket photo gate. The locket block owns the rule and the copy; we only
    // mirror the wording so the bar never offers an add it cannot complete. It is
    // an event, not a poll, and it is a no-op on every product without a locket.
    document.addEventListener('mab:locket:gate', this.#handleLocketGate, { signal });

    this.#getInitialQuantity();
    this.#syncGateFromDom();
  }

  /**
   * One-time reconciliation for load order. The locket block and this component
   * are separate deferred scripts, so the locket's first gate event can land
   * before we are listening. updateGate() also stamps mab-atc-gated onto our
   * button, so that class is a reliable record of a gate event we may have
   * missed. Read once on connect, then rely on the event.
   */
  #syncGateFromDom() {
    const button = this.refs.addToCartButton;
    if (!button?.classList.contains('mab-atc-gated')) return;
    const locket = document.querySelector('mab-locket-upload');
    this.#gate = { ready: false, label: locket?.dataset.stickyLabel || 'Add your photo' };
    this.#applyGateLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#columnObserver?.disconnect();
    this.#columnObserver = null;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    // Observer for the real add to cart button. We track the button itself rather
    // than the whole .buy-buttons-block, so the bar stands in for exactly the
    // control it replaces.
    //
    // Visibility is taken straight from entry.isIntersecting, in BOTH directions.
    // The previous version added a getBoundingClientRect test that only showed the
    // bar when the button was ABOVE the viewport, so on a page where the button
    // starts below the fold, which is every phone and any laptop shorter than the
    // details column, the bar could never appear on the way down.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      this.#targetVisible = entry.isIntersecting;
      this.#updateStickyVisibility();
    });

    // Observer for the footer, which hides the bar at the true page bottom.
    //
    // The margin is on the BOTTOM and negative, which is the only edge that can
    // delay something entering from below. The old '200px 0px 0px 0px' expanded
    // the top edge, which does nothing to entry from the bottom and only ever made
    // suppression start earlier.
    //
    // Why it matters: the footer is tall relative to a phone page. On the locket
    // at 390x844 it is 798px starting at 1930, on a 2869px page, so by any plain
    // "is the footer on screen" test it counts as visible from scrollY 1086. The
    // real button does not leave the viewport until 1433, so the bar could never
    // appear below the button at all, and the recommendations at 1601 to 1930 were
    // entirely inside the suppressed zone.
    //
    // Shrinking the root to its top 15 percent asks a stricter question: has the
    // footer risen far enough to own the screen. That defers suppression to scrollY
    // 1803 there, so the bar survives the recommendations and hides only over the
    // last 220px. Percentages scale with the viewport, so no resize recompute.
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.#footerVisible = entry.isIntersecting;
        this.#updateStickyVisibility();
      },
      { rootMargin: '0px 0px -85% 0px' }
    );

    this.#observeTarget();
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Points the button observer at the current real add to cart button. Called
   * again after a variant update, because the product form is re-rendered and the
   * old button node is discarded, which would leave us observing a detached node.
   */
  #observeTarget() {
    if (!this.#buyButtonsIntersectionObserver) return;
    this.#buyButtonsIntersectionObserver.disconnect();
    if (!this.#targetAddToCartButton) return;
    this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);
  }

  /**
   * Single place that decides whether the bar is up. Show when the real button is
   * off screen in either direction and the footer is not in view.
   *
   * That holds at the top of the page too (owner ruling, 4 Oct): on a short
   * laptop window the real button starts below the fold and the bar is up on
   * arrival. #alignToDetails is what keeps it off the photo, wherever the
   * page is.
   */
  #updateStickyVisibility() {
    const shouldShow = !this.#targetVisible && !this.#footerVisible;
    if (shouldShow === this.#isStuck) return;
    if (shouldShow) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  /**
   * Centres the bar on the details column's content box, as --mab-bar-centre
   * and --mab-bar-room on the bar. The stylesheet uses them from 750px up only,
   * so phones keep the full width bar. Centred on the window, the 600px bar
   * reached into the photo column and covered the bottom of a portrait photo
   * whenever it was up. The column's padding (0 left, 16px right here) is left
   * out, so the bar is centred on the box its content sits in rather than on
   * the padded box, and the room is floored, so the bar is never a fraction
   * wider than that box.
   */
  #alignToDetails() {
    const bar = this.refs.stickyBar;
    const details = this.closest('.shopify-section')?.querySelector('.product-information .product-details');
    if (!bar || !(details instanceof HTMLElement)) return;
    const box = details.getBoundingClientRect();
    const style = getComputedStyle(details);
    const left = box.left + (parseFloat(style.paddingLeft) || 0);
    const right = box.right - (parseFloat(style.paddingRight) || 0);
    if (right - left <= 0) return;
    bar.style.setProperty('--mab-bar-centre', `${Math.round((left + right) / 2)}px`);
    bar.style.setProperty('--mab-bar-room', `${Math.floor(right - left)}px`);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#awaitingAdd = true;
    this.#targetAddToCartButton.click();
  };

  /**
   * The celebration belongs to the answer, not to the tap.
   *
   * This used to run the moment the button was clicked: the checkmark burst and
   * the image flew into the cart icon whether or not anything reached the cart.
   * Shopify's upload path times out often enough on a photo locket to see it in
   * one evening, so a buyer could watch the animation over an empty cart.
   */
  #celebrateAdd = async () => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      // The form was re-rendered, so the node we were observing is gone.
      this.#observeTarget();
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    // The morph rebuilt the label from the server, so re-apply the gate wording.
    this.#ungatedLabel = null;
    this.#applyGateLabel();
    // The details column can change height but rarely width here; measure
    // again anyway, it is one rect read.
    this.#alignToDetails();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Mirrors the locket photo gate onto the bar's label.
   *
   * Label only. The click path is untouched: the locket block's own capture
   * listener already blocks the add and scrolls its upload zone into view, and it
   * scopes that to this product's form and its sticky bar. Duplicating that here
   * would give two things the power to cancel one click.
   *
   * @param {CustomEvent} event
   */
  #handleLocketGate = (event) => {
    const detail = event.detail;
    if (!detail) return;
    if (String(detail.productId) !== String(this.dataset.productId)) return;
    this.#gate = { ready: !!detail.ready, label: detail.label || 'Add your photo' };
    this.#applyGateLabel();
  };

  /**
   * Swaps the label between the gated wording and whatever the server rendered.
   * Only the text node changes, so the quantity badge, the icon and the added
   * state are left alone.
   */
  #applyGateLabel() {
    const content = this.refs.addToCartButton?.querySelector('.add-to-cart-text__content > span:first-child');

    if (content) {
      if (this.#ungatedLabel == null) this.#ungatedLabel = content.textContent;

      if (this.#gate && !this.#gate.ready) {
        if (content.textContent !== this.#gate.label) content.textContent = this.#gate.label;
      } else if (content.textContent !== this.#ungatedLabel) {
        content.textContent = this.#ungatedLabel;
      }
    }

    this.#syncAccessibleName();
  }

  /**
   * Keeps the button's accessible name equal to the words on it. Below 750px the
   * bar hides .add-to-cart-text__content and shows the icon alone, so without
   * this the button has no name and fails button-name. The name is built from
   * the label the server rendered, or from the gated wording when the locket
   * gate is closed, plus the quantity while the quantity badge is showing, so a
   * screen reader and a pair of eyes get the same sentence.
   */
  #syncAccessibleName() {
    const button = this.refs.addToCartButton;
    if (!button) return;

    const content = button.querySelector('.add-to-cart-text__content > span:first-child');
    let name = content?.textContent?.trim() || button.getAttribute('aria-label')?.trim() || '';
    if (!name) return;

    const quantity = this.refs.quantityDisplay;
    if (quantity && quantity.style.display !== 'none') {
      const badge = quantity.textContent?.trim();
      if (badge) name = `${name} ${badge}`;
    }

    if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#awaitingAdd) return;
    this.#awaitingAdd = false;
    // A cart error, or an add that answered with didError, is not a reason to
    // burst a checkmark.
    if (event?.type === ThemeEvents.cartError) return;
    if (event?.detail?.data?.didError) return;
    this.#celebrateAdd();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }

    // The visible words just changed, so the name has to change with them.
    this.#syncAccessibleName();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
